import { GUID } from "@faro-lotv/foundation";
import { useCallback, useMemo } from "react";
import { temporal, TemporalState } from "zundo";
import { create, useStore } from "zustand";
import { ManualBlur } from "./manual-blurring-types";

export type ManualBlurringState = {
  /** Map of elements to their manual blurs */
  manualBlurs: Record<GUID, ManualBlur[] | undefined>;

  /** Map of elements to their  currently saving manual blurs */
  currentlySavingManualBlurs: Record<GUID, ManualBlur[] | undefined>;

  /** Add a newly created manual blur to a specific element */
  addManualBlur(elementId: GUID, manualBlur: ManualBlur): void;

  /** Moves an element's blurs form the pending blurs into the currently saving blurs. */
  startSavingManualBlurs(elementId: GUID): void;

  /** Removes the manual blurs from the currently saving list */
  finishSavingManualBlurs(elementId: GUID, blurIds: GUID[]): void;
};

/** The data in the state that is tracked for the undo/redo feature */
type TemporalBlurringState = Pick<ManualBlurringState, "manualBlurs">;

/** Returns the zustand store for the manual blurring tool. Uses zundo, so actions should map into un-doable steps. */
export const useManualBlurringStore = create<ManualBlurringState>()(
  temporal(
    (set) => ({
      manualBlurs: {},
      currentlySavingManualBlurs: {},
      addManualBlur(elementId: GUID, manualBlur: ManualBlur) {
        set((state) => ({
          manualBlurs: {
            ...state.manualBlurs,
            [elementId]: [...(state.manualBlurs[elementId] ?? []), manualBlur],
          },
        }));
      },
      startSavingManualBlurs(elementId: GUID) {
        set((state) => {
          const { [elementId]: blursToSave, ...remainingBlurs } =
            state.manualBlurs;

          return {
            manualBlurs: remainingBlurs,
            currentlySavingManualBlurs: {
              ...state.currentlySavingManualBlurs,
              [elementId]: [
                ...(state.currentlySavingManualBlurs[elementId] ?? []),
                ...(blursToSave ?? []),
              ],
            },
          };
        });
      },
      finishSavingManualBlurs(elementId: GUID, blurIds: GUID[]) {
        set((state) => {
          const currentlySaving = state.currentlySavingManualBlurs[elementId];
          if (!currentlySaving) return state;

          return {
            currentlySavingManualBlurs: {
              ...state.currentlySavingManualBlurs,
              [elementId]: currentlySaving.filter(
                (blur) => !blurIds.includes(blur.id),
              ),
            },
          };
        });
      },
    }),
    {
      // Only the currently drawn blurs should be undo-able
      partialize: ({ manualBlurs }): TemporalBlurringState => ({ manualBlurs }),
    },
  ),
);

/**
 * @returns Functions and state to control undo/redo state for the manual blurring tool.
 */
export function useManualBlurringUndoRedo(): TemporalState<TemporalBlurringState> {
  return useStore(useManualBlurringStore.temporal);
}

/**
 * @returns Function to reset the store of all not currently saving manual blurs and clear all undo history.
 */
export function useResetManualBlurringStore(): () => void {
  const { clear } = useManualBlurringUndoRedo();
  return useCallback(() => {
    useManualBlurringStore.setState({
      manualBlurs: {},
    });
    clear();
  }, [clear]);
}

/** @returns The number of distinct panoramas that have pending blurs in the editing state. */
export function useNumPanosBlurred(): number {
  const { manualBlurs } = useManualBlurringStore();

  return useMemo(
    () =>
      Object.values(manualBlurs).filter((blurs) => !!blurs && blurs.length > 0)
        .length,
    [manualBlurs],
  );
}

/**
 * @returns whether the manual blurs state has unsaved changes
 * @param state The current state of the manual blurs store
 */
export function selectHasManualBlurringStoreUnsavedChanges(
  state: ManualBlurringState,
): boolean {
  return Object.values(state.manualBlurs).some(
    (blurs) => !!blurs && blurs.length > 0,
  );
}
